import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ClientPortalAuthState, ClientPortalUser } from '../types';
import { clientPortalApi } from '../services/api';

interface ClientPortalAuthContextType extends ClientPortalAuthState {
  login: (token: string, user: ClientPortalUser) => void;
  logout: () => void;
  updateUser: (user: ClientPortalUser) => void;
}

const ClientPortalAuthContext = createContext<ClientPortalAuthContextType | undefined>(undefined);

function getTokenExpiration(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }

    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const exp = getTokenExpiration(token);
  if (exp === null) {
    return false;
  }

  return Date.now() >= exp * 1000;
}

export const ClientPortalAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<ClientPortalAuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
  });

  useEffect(() => {
    const token = localStorage.getItem('client_portal_token');
    const userStr = localStorage.getItem('client_portal_user');

    if (!token || !userStr) {
      return;
    }

    if (isTokenExpired(token)) {
      localStorage.removeItem('client_portal_token');
      localStorage.removeItem('client_portal_user');
      return;
    }

    try {
      const user = JSON.parse(userStr) as ClientPortalUser;
      setAuthState({
        isAuthenticated: true,
        user,
        token,
      });
    } catch {
      localStorage.removeItem('client_portal_token');
      localStorage.removeItem('client_portal_user');
    }
  }, []);

  const login = (token: string, user: ClientPortalUser) => {
    localStorage.setItem('client_portal_token', token);
    localStorage.setItem('client_portal_user', JSON.stringify(user));
    setAuthState({
      isAuthenticated: true,
      user,
      token,
    });
  };

  const logout = () => {
    void clientPortalApi.logout().catch(() => undefined);
    localStorage.removeItem('client_portal_token');
    localStorage.removeItem('client_portal_user');
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
    });
  };

  const updateUser = (user: ClientPortalUser) => {
    localStorage.setItem('client_portal_user', JSON.stringify(user));
    setAuthState((prev) => ({
      ...prev,
      user,
    }));
  };

  return (
    <ClientPortalAuthContext.Provider value={{ ...authState, login, logout, updateUser }}>
      {children}
    </ClientPortalAuthContext.Provider>
  );
};

export const useClientPortalAuth = () => {
  const context = useContext(ClientPortalAuthContext);
  if (!context) {
    throw new Error('useClientPortalAuth must be used within a ClientPortalAuthProvider');
  }

  return context;
};
